<template>
  <div>
    <div style="display:flex">
      <button
        ref="trigger"
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-activedescendant="open ? optionId(activeIndex) : null"
        :aria-label="`Phone number country, ${selected.name} selected`"
        :style="triggerStyle"
        @click="toggle"
        @keydown="onTriggerKey"
      >
        <span :style="flagStyle">
          <!-- eslint-disable vue/no-v-html -->
          <svg
            v-if="selected.flag"
            :viewBox="flagViewBox"
            class="h-full w-full"
            aria-hidden="true"
            v-html="selected.flag"
          />
          <!-- eslint-enable vue/no-v-html -->
        </span>
        <span style="flex:1 1 auto;text-align:left">{{ selected.label }}</span>
        <svg
          :style="chevronStyle"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            d="M5.5 7.5L10 12l4.5-4.5"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <input
        ref="field"
        v-model="text"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        autocorrect="false"
        spellcheck="false"
        maxlength="32"
        :placeholder="placeholder"
        :style="inputStyle"
        @beforeinput="onBeforeInput"
        @input="onInput"
        @blur="onBlur"
      >
    </div>

    <ul
      v-if="open"
      ref="list"
      role="listbox"
      :style="popupStyle"
    >
      <li
        v-for="(opt, i) in options"
        :id="optionId(i)"
        :key="opt.code"
        role="option"
        :aria-label="opt.name"
        :aria-selected="opt.code === country ? 'true' : 'false'"
        :style="optionStyle(i)"
        @mousemove="activeIndex = i"
        @mousedown.prevent="pick(opt.code)"
      >
        <span :style="flagStyle">
          <!-- eslint-disable vue/no-v-html -->
          <svg
            v-if="opt.flag"
            :viewBox="flagViewBox"
            class="h-full w-full"
            aria-hidden="true"
            v-html="opt.flag"
          />
          <!-- eslint-enable vue/no-v-html -->
        </span>
        <span>{{ opt.label }}</span>
      </li>
    </ul>

    <div
      v-if="visibleError"
      style="margin-top:0.25rem;font-size:0.75rem;color:#e53e3e"
    >
      {{ visibleError }}
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_COUNTRY,
  FLAG_VIEWBOX,
  PHONE_COUNTRIES,
  countryFlagSvg,
  phoneErrorMessage,
  splitE164,
  toE164,
} from 'pmg-services';

// Digits plus the punctuation people write numbers with. Letters never belong
// in a phone number, so they are refused at the keystroke.
const TYPEABLE = /^[\d\s()+.-]+$/;

// This app is on Tailwind 1, which has no gap, ring or arbitrary-value
// utilities, so the control geometry is set inline rather than by class.
const TRIGGER_STYLE = 'display:flex;align-items:center;width:5.5rem;flex:none;padding:0.5rem;font-size:0.75rem;color:#4a5568;background:#f7fafc;border-width:thin;border-right:0;';
const INPUT_STYLE = "width:100%;padding:0.5rem;font-size:0.875rem;border-width:thin;font-family:'Roboto', sans-serif;";
const FLAG_STYLE = 'width:1.35rem;height:0.95rem;flex:none;overflow:hidden;margin-right:0.375rem;box-shadow:0 0 0 1px rgba(0,0,0,.1);';
const CHEVRON_STYLE = 'width:0.75rem;height:0.75rem;flex:none;opacity:0.6;';
const LIST_STYLE = {
  position: 'fixed',
  zIndex: 50,
  maxHeight: '16rem',
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '0.25rem',
  padding: '0.25rem 0',
  boxShadow: '0 4px 6px rgba(0,0,0,.1)',
};
const OPTION_STYLE = 'display:flex;align-items:center;padding:0.375rem 0.75rem;font-size:0.875rem;cursor:pointer;';

export default {
  name: 'PmgPhoneInput',

  props: {
    /** Canonical E.164 value, or a legacy national number. */
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Mobile Number',
    },
    /** Host is already showing the message itself, e.g. after a submit. */
    showError: {
      type: Boolean,
      default: false,
    },
    /** A blank entry is acceptable, e.g. the second number on a profile. */
    optional: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      country: DEFAULT_COUNTRY,
      text: '',
      message: '',
      // Judge the entry only once the member has had a fair chance at it.
      touched: false,
      everValid: false,
      open: false,
      activeIndex: 0,
      popupStyle: {},
      // Guards the value watcher against overwriting what the user is typing.
      lastEmitted: '',
      uid: `phone-${Math.random().toString(36).slice(2, 8)}`,
    };
  },

  computed: {
    flagViewBox() {
      return FLAG_VIEWBOX;
    },

    entry() {
      return PHONE_COUNTRIES.find(c => c.code === this.country) || PHONE_COUNTRIES[0];
    },

    /** Longest national spelling we could still accept, trunk zero included. */
    maxNationalDigits() {
      return this.entry.max + (this.entry.trunk ? 1 : 0);
    },

    visibleError() {
      return (this.touched || this.showError) && this.message ? this.message : '';
    },

    options() {
      return PHONE_COUNTRIES.map(c => ({
        code: c.code,
        label: `+${c.dial}`,
        name: c.name,
        flag: countryFlagSvg(c.code),
      }));
    },

    selected() {
      return this.options.find(o => o.code === this.country) || this.options[0];
    },

    flagStyle() {
      return FLAG_STYLE;
    },

    chevronStyle() {
      return CHEVRON_STYLE;
    },

    triggerStyle() {
      return TRIGGER_STYLE;
    },

    inputStyle() {
      return INPUT_STYLE;
    },
  },

  watch: {
    value(next) {
      if (next === this.lastEmitted) return;
      // A change from outside is a fresh entry: judge it again from scratch.
      this.hydrate(next);
      this.touched = false;
      this.everValid = false;
      this.emit();
    },
  },

  mounted() {
    this.hydrate(this.value);
    this.emit();
  },

  beforeDestroy() {
    this.unbindDismiss();
  },

  methods: {
    optionStyle(i) {
      return OPTION_STYLE + (i === this.activeIndex ? 'background:#edf2f7;' : '');
    },

    optionId(i) {
      return `${this.uid}-opt-${i}`;
    },

    hydrate(value) {
      const parts = splitE164(value);
      this.country = parts.country;
      this.text = parts.national;
    },

    toggle() {
      if (this.open) this.close();
      else this.openList();
    },

    openList() {
      this.activeIndex = Math.max(
        0,
        this.options.findIndex(o => o.code === this.country),
      );
      this.open = true;
      this.position();
      this.bindDismiss();
    },

    close() {
      this.open = false;
      this.unbindDismiss();
    },

    /** Fixed positioning, so a clipping ancestor cannot cut the list off. */
    position() {
      const el = this.$refs.trigger;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const width = Math.max(r.width, 112);
      this.popupStyle = below < 220 && r.top > below
        ? {
          ...LIST_STYLE,
          left: `${r.left}px`,
          bottom: `${window.innerHeight - r.top + 4}px`,
          width: `${width}px`,
        }
        : {
          ...LIST_STYLE,
          left: `${r.left}px`,
          top: `${r.bottom + 4}px`,
          width: `${width}px`,
        };
    },

    bindDismiss() {
      this.onDocPointer = (e) => {
        if (!this.$el.contains(e.target)) this.close();
      };
      // Keep the list glued to the trigger rather than closing underneath it.
      this.onWinChange = () => this.position();
      document.addEventListener('mousedown', this.onDocPointer, true);
      window.addEventListener('scroll', this.onWinChange, true);
      window.addEventListener('resize', this.onWinChange);
    },

    unbindDismiss() {
      if (this.onDocPointer) {
        document.removeEventListener('mousedown', this.onDocPointer, true);
        this.onDocPointer = null;
      }
      if (this.onWinChange) {
        window.removeEventListener('scroll', this.onWinChange, true);
        window.removeEventListener('resize', this.onWinChange);
        this.onWinChange = null;
      }
    },

    move(step) {
      const last = this.options.length - 1;
      this.activeIndex = Math.min(last, Math.max(0, this.activeIndex + step));
      this.$nextTick(() => {
        const { list } = this.$refs;
        const el = list && list.children[this.activeIndex];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    },

    onTriggerKey(e) {
      if (!this.open) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
          e.preventDefault();
          this.openList();
        }
        return;
      }
      if (e.key === 'Escape' || e.key === 'Tab') {
        this.close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.move(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.move(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.activeIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        this.activeIndex = this.options.length - 1;
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.pick(this.options[this.activeIndex].code);
      }
    },

    pick(code) {
      this.country = code;
      this.close();
      this.emit();
      this.$nextTick(() => {
        if (this.$refs.trigger) this.$refs.trigger.focus();
      });
    },

    onBeforeInput(e) {
      // Single typed characters only. Multi character insertions (paste,
      // autofill, some keyboards) fall through and get scrubbed instead, so
      // "07123 456789 (mobile)" keeps its number rather than being refused.
      if (e.inputType !== 'insertText' || e.data == null) return;
      if (e.data.length !== 1) return;
      if (!TYPEABLE.test(e.data)) e.preventDefault();
    },

    onInput() {
      this.scrub();
      // Stay quiet while a number can still be completed; speak up the moment
      // extra digits cannot rescue it, or a good number has been broken.
      const digits = this.text.replace(/\D/g, '');
      if (this.everValid || digits.length > this.maxNationalDigits) {
        this.touched = true;
      }
      this.emit();
    },

    /** Drop anything unpasteable, keeping the caret where the member left it. */
    scrub() {
      const el = this.$refs.field;
      const raw = this.text;
      const clean = this.allowedOnly(raw);
      if (clean === raw) return;
      const caret = el && el.selectionStart !== null ? el.selectionStart : raw.length;
      const kept = this.allowedOnly(raw.slice(0, caret)).length;
      this.text = clean;
      if (!el) return;
      this.$nextTick(() => {
        el.value = clean;
        el.setSelectionRange(kept, kept);
      });
    },

    allowedOnly(value) {
      const kept = value.replace(/[^\d\s()+.-]/g, '');
      // A plus only means anything as the first character.
      return kept.charAt(0) + kept.slice(1).replace(/\+/g, '');
    },

    onBlur() {
      // Leaving a field the member actually typed in is a fair moment to judge.
      if (this.text.length > 0) this.touched = true;
      // Snap a valid entry to its canonical spelling so the member sees what
      // is stored, and leave an invalid entry alone so it stays correctable.
      const result = toE164(this.text, this.country);
      if (result.e164) {
        const parts = splitE164(result.e164);
        this.country = parts.country;
        this.text = parts.national;
      }
      this.emit();
    },

    emit() {
      if (this.optional && this.text.trim() === '') {
        this.lastEmitted = '';
        this.message = '';
        this.$emit('update:value', '');
        this.$emit('update:valid', true);
        this.$emit('update:message', '');
        return;
      }
      const result = toE164(this.text, this.country);
      if (result.e164) this.everValid = true;
      this.lastEmitted = result.e164 || '';
      this.message = result.e164
        ? ''
        : phoneErrorMessage(result.reason, this.country);
      this.$emit('update:value', this.lastEmitted);
      this.$emit('update:valid', result.e164 !== null);
      this.$emit('update:message', this.message);
    },
  },
};
</script>
